import { requestJson } from '../api/http';
import {
  noteDetailSchema,
  readyResponseSchema,
  type NoteDetail,
  type ReadyResult,
  type ReviewSaveRequestBody,
} from './schema';

/** `GET /notes/{id}` - everything the review screen needs for one note (`app/api/review.py`). */
export function fetchNoteDetail(noteId: string): Promise<NoteDetail> {
  return requestJson(`/notes/${noteId}`, noteDetailSchema);
}

/**
 * `PUT /notes/{id}/review` - saves a complete reviewed draft as a new revision.
 *
 * Refusals the caller must classify by `ApiError.status`/`code`:
 * - `409 revision_conflict` - another session saved a newer revision first.
 * - `409 note_not_reviewable` - the note left an editable status while this was open.
 * - `422 invalid_reference` - the document names a customer/product NoteVision never issued.
 */
export function saveReview(noteId: string, body: ReviewSaveRequestBody): Promise<NoteDetail> {
  return requestJson(`/notes/${noteId}/review`, noteDetailSchema, { method: 'PUT', body });
}

/**
 * `POST /notes/{id}/ready` - the owner's explicit **Lista para Excel** approval
 * (`app/api/ready.py`). Answers `201` for a first approval and `200` for an idempotent replay
 * of the exact same one, with the same body either way - this function does not distinguish
 * them, matching `saveReview`'s "the body is the truth" convention.
 *
 * Refusals the caller must classify by `ApiError.status`/`code` (see `review/readyFailure.ts`):
 * - `409 nothing_to_approve` - no revision has been saved yet.
 * - `422 note_incomplete` - a required field is missing for the existing sales form.
 * - `409 reference_blocked` - an unresolved customer or product still blocks delivery.
 * - `409 automatic_review_disabled` - the active revision was not reviewed by a human.
 * - `409 note_not_approvable` - the note is not in a state this action can act on.
 */
export function markReady(noteId: string): Promise<ReadyResult> {
  return requestJson(`/notes/${noteId}/ready`, readyResponseSchema, { method: 'POST' });
}
